import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  KeyRound, Plus, Trash2, Copy, Check, ShieldAlert,
  ArrowLeft, RefreshCw, X,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuthStore, useLanguageStore } from '../store';
import { useTranslation } from '../i18n/translations';
import { SEO } from '../components/SEO';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ALL_SCOPES = ['read:catalog', 'read:collections'];

const CreateKeyModal = ({ open, onClose, onCreated, token, l }) => {
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState(['read:catalog', 'read:collections']);
  const [saving, setSaving] = useState(false);
  const [createdKey, setCreatedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    if (!label.trim()) { toast.error('Label is required'); return; }
    if (!scopes.length) { toast.error('Pick at least one scope'); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/b2b/api-keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ label: label.trim(), scopes }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.detail || 'Failed to create key');
      }
      const data = await res.json();
      setCreatedKey(data.key);
      onCreated();
    } catch (err) {
      toast.error(err.message || 'Failed to create key');
    } finally {
      setSaving(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(createdKey);
      setCopied(true);
      toast.success(l?.copied || 'Copied to clipboard');
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error('Clipboard write failed:', err);
      toast.error('Clipboard unavailable');
    }
  };

  const toggleScope = (scope) => {
    setScopes((prev) => (prev.includes(scope) ? prev.filter((s) => s !== scope) : [...prev, scope]));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A]/40 backdrop-blur-sm flex items-center justify-center p-4" data-testid="apikey-create-modal">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white w-full max-w-lg border border-[#ECECEC] shadow-2xl rounded-none"
      >
        <div className="flex items-center justify-between border-b border-[#ECECEC] p-6 bg-[#FAFAFA]">
          <div>
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-1 font-medium">
              {createdKey ? (l?.keyGenerated || 'Key generated') : (l?.newApiKey || 'New API key')}
            </p>
            <h2 className="text-2xl font-light text-[#0A0A0A]">
              {createdKey ? (l?.saveYourKey || 'Save your key') : (l?.keyGeneratedDesc || 'Create API key')}
            </h2>
          </div>
          <button onClick={onClose} className="text-[#9E9E9E] hover:text-[#0A0A0A] transition-colors" data-testid="apikey-modal-close">
            <X className="w-5 h-5" strokeWidth={1.5} />
          </button>
        </div>

        {createdKey ? (
          <div className="p-6 space-y-5">
            <div className="border border-[#ECECEC] bg-[#FAFAFA] p-4">
              <p className="text-[9px] tracking-[0.2em] uppercase text-[#D32F2F] mb-3 flex items-center gap-1.5 font-semibold">
                <ShieldAlert className="w-3.5 h-3.5" strokeWidth={2} /> {l?.showOnceWarning || 'Shown only once · store it now'}
              </p>
              <code className="block font-mono text-xs text-[#0A0A0A] break-all leading-relaxed bg-white border border-[#ECECEC] p-3" data-testid="apikey-value">{createdKey}</code>
            </div>
            <Button onClick={handleCopy} variant="outline" className="w-full rounded-none border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white text-xs tracking-wider font-light h-11 transition-colors" data-testid="apikey-copy-btn">
              {copied ? (
                <><Check className="w-4 h-4 mr-2 text-green-600" strokeWidth={1.5} /> {l?.copied || 'Copied'}</>
              ) : (
                <><Copy className="w-4 h-4 mr-2" strokeWidth={1.5} /> {l?.copyToClipboard || 'Copy to clipboard'}</>
              )}
            </Button>
            <p className="text-xs font-light text-[#4A4A4A] leading-relaxed">
              {l?.documentationDesc || 'Use the header X-API-Key or Authorization Bearer when calling your endpoints.'}
            </p>
            <Button onClick={onClose} className="rounded-none bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white text-[11px] tracking-[0.25em] uppercase w-full py-4 transition-colors font-medium" data-testid="apikey-modal-done">
              {l?.savedWarning || 'I have saved it'}
            </Button>
          </div>
        ) : (
          <div className="p-6 space-y-5">
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-[#9E9E9E] block mb-2 font-medium">{l?.label || 'Label'} *</label>
              <Input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="e.g. Revit plugin / Studio dashboard"
                className="rounded-none h-11 border-[#ECECEC] focus-visible:border-[#0A0A0A]"
                maxLength={120}
                data-testid="apikey-label"
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.18em] uppercase text-[#9E9E9E] block mb-2 font-medium">{l?.scopes || 'Scopes'}</label>
              <div className="space-y-3 bg-[#FAFAFA] border border-[#ECECEC] p-4">
                {ALL_SCOPES.map((s) => (
                  <label key={s} className="flex items-center gap-3 cursor-pointer text-sm text-[#0A0A0A]">
                    <input
                      type="checkbox"
                      checked={scopes.includes(s)}
                      onChange={() => toggleScope(s)}
                      className="accent-black w-4 h-4"
                      data-testid={`apikey-scope-${s.replace(':', '-')}`}
                    />
                    <code className="bg-white border border-[#ECECEC] px-2 py-0.5 text-xs font-mono">{s}</code>
                    <span className="text-[#4A4A4A] text-xs font-light">
                      {s === 'read:catalog' ? (l?.readCatalogDesc || 'Read products + trade pricing') : (l?.readCollectionsDesc || 'Read collections')}
                    </span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#ECECEC]">
              <Button variant="outline" className="rounded-none border-[#ECECEC] text-xs uppercase tracking-wider" onClick={onClose} disabled={saving}>
                {l?.cancel || 'Cancel'}
              </Button>
              <Button onClick={handleCreate} disabled={saving} className="rounded-none bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white text-[11px] tracking-[0.15em] uppercase py-3 px-5 font-medium transition-colors" data-testid="apikey-create-submit">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                {saving ? (l?.generating || 'Generating…') : (l?.generateKey || 'Generate Key')}
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

const B2BApiKeysPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const { user, token, isAuthenticated } = useAuthStore();
  
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);

  const l = t.b2bApiKeys || {};

  useEffect(() => {
    if (!isAuthenticated) {
      navigate(`/${language}/login`);
      return;
    }
    if (!user?.is_b2b) {
      navigate(`/${language}/b2b`);
    }
  }, [isAuthenticated, user, navigate, language]);

  // Оборачиваем функцию получения ключей в useCallback, чтобы избежать лишней регенерации
  const fetchKeys = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/b2b/api-keys`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setKeys(data.keys || []);
    } catch (err) {
      toast.error('Failed to load keys');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { 
    fetchKeys(); 
  }, [fetchKeys]);

  const revoke = async (keyId, label) => {
    const confirmText = l?.revokeConfirm ? l.revokeConfirm.replace('"{label}"', `"${label}"`) : `Revoke "${label}"?`;
    if (!window.confirm(confirmText)) return;
    try {
      const res = await fetch(`${API}/api/b2b/api-keys/${keyId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      toast.success('Revoked');
      fetchKeys();
    } catch (err) {
      toast.error('Failed to revoke');
    }
  };

  if (!isAuthenticated || !user?.is_b2b) return null;

  const isApproved = user.is_approved;

  return (
    <div className="min-h-screen bg-[#F4F2EE] text-[#0A0A0A] font-sans pb-24" data-testid="b2b-api-keys-page">
      <SEO title="API Keys · Trade Portal" description="Manage your B2B API keys" path="b2b/api-keys" />

      {/* Header Profile Section */}
      <div className="bg-white border-b border-[#ECECEC] pt-28 pb-12 mb-12">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <Link
            to={`/${language}/b2b/dashboard`}
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.15em] uppercase text-[#9E9E9E] hover:text-[#0A0A0A] transition-colors mb-8 font-medium"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={1.5} />
            {l?.backToDashboard || 'Back to dashboard'}
          </Link>

          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
            <div>
              <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-3 flex items-center gap-2 font-medium">
                <KeyRound className="w-3 h-3 text-[#9E9E9E]" strokeWidth={1.5} /> {l?.eyebrow || 'Trade Integration'}
              </p>
              <h1 className="text-4xl md:text-5xl font-extralight tracking-tight text-[#0A0A0A] mb-4">
                {l?.title || 'API Keys'}
              </h1>
              <p className="max-w-xl text-sm md:text-base font-light text-[#4A4A4A] leading-relaxed">
                {l?.subtitle || 'Issue read-only keys for your design tools — Revit, ArchiCAD, in-house dashboards. Each key inherits your trade access.'}
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <Button variant="outline" onClick={fetchKeys} disabled={loading} className="rounded-none border-[#ECECEC] hover:bg-[#FAFAFA] text-xs uppercase tracking-wider px-4 py-2.5 font-light text-[#0A0A0A]">
                <RefreshCw className={`w-3.5 h-3.5 mr-2 ${loading ? 'animate-spin' : ''}`} strokeWidth={1.5} />Refresh
              </Button>
              <Button
                onClick={() => setModalOpen(true)}
                disabled={!isApproved}
                className="rounded-none bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white text-[11px] tracking-[0.15em] uppercase py-3.5 px-5 transition-colors font-medium disabled:opacity-50"
                data-testid="apikey-new-btn"
              >
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                {l?.newApiKey || 'New API Key'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 lg:px-10">
        {!isApproved && (
          <div className="bg-white border-l-2 border-[#0A0A0A] border-y border-r border-[#ECECEC] p-6 mb-10 shadow-sm" data-testid="apikey-pending-banner">
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-2 font-medium">
              {l?.pendingTitle || 'Pending approval'}
            </p>
            <p className="text-sm font-light text-[#4A4A4A] leading-relaxed">
              {l?.pendingText || 'Your B2B account is awaiting verification.'}
            </p>
          </div>
        )}

        {loading ? (
          <div className="py-24 text-center text-sm font-light text-[#4A4A4A]">Loading…</div>
        ) : keys.length === 0 ? (
          <div className="py-24 text-center text-[#4A4A4A] bg-white border border-[#ECECEC] shadow-sm" data-testid="apikey-empty-state">
            <p className="text-sm font-light">{l?.emptyState || 'No API keys generated yet.'}</p>
            {isApproved && (
              <div className="mt-6">
                <Button onClick={() => setModalOpen(true)} className="rounded-none bg-[#0A0A0A] hover:bg-[#1A1A1A] text-white text-[11px] tracking-[0.25em] uppercase py-3 px-6 transition-colors font-medium">
                  <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.5} />
                  {l?.firstKeyBtn || 'Generate your first key'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="bg-white border border-[#ECECEC] shadow-sm">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-[#ECECEC] bg-[#FAFAFA] text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] font-medium">
              <div className="col-span-4">{l?.tableKey || 'Key'}</div>
              <div className="col-span-3">{l?.scopes || 'Scopes'}</div>
              <div className="col-span-2 text-right">{l?.tableUsage || 'Usage'}</div>
              <div className="col-span-2">{l?.tableLastUsed || 'Last used'}</div>
              <div className="col-span-1 text-right">·</div>
            </div>
            {keys.map((k) => (
              <div
                key={k.id}
                className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-[#ECECEC] last:border-b-0 items-center text-sm text-[#0A0A0A]"
                data-testid={`apikey-row-${k.id}`}
              >
                <div className="col-span-4">
                  <p className="font-medium text-base mb-0.5">{k.label}</p>
                  <code className="text-xs text-[#9E9E9E] font-mono tracking-wide">{k.prefix}…</code>
                  {!k.is_active && (
                    <span className="ml-2 text-[9px] tracking-wider uppercase bg-[#0A0A0A] text-white px-2 py-0.5 font-medium">
                      {l?.revoked || 'Revoked'}
                    </span>
                  )}
                </div>
                <div className="col-span-3 flex flex-wrap gap-1.5">
                  {(k.scopes || []).map((s) => (
                    <code key={s} className="bg-[#FAFAFA] border border-[#ECECEC] text-[11px] text-[#4A4A4A] px-2 py-0.5 font-medium">
                      {s}
                    </code>
                  ))}
                </div>
                <div className="col-span-2 text-right tabular-nums text-[#4A4A4A] font-light">{k.usage_count || 0}</div>
                <div className="col-span-2 text-xs text-[#4A4A4A] font-light">
                  {k.last_used_at ? new Date(k.last_used_at).toLocaleDateString() : '—'}
                </div>
                <div className="col-span-1 text-right">
                  {k.is_active && (
                    <button
                      onClick={() => revoke(k.id, k.label)}
                      className="text-[#9E9E9E] hover:text-[#D32F2F] transition-colors"
                      title="Revoke"
                      data-testid={`apikey-revoke-${k.id}`}
                    >
                      <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Developer Footer Section */}
        <div className="mt-16 pt-10 border-t border-[#ECECEC]">
          <p className="text-[10px] tracking-[0.25em] uppercase text-[#9E9E9E] mb-4 font-medium">
            {l?.documentationTitle || 'Quick start documentation'}
          </p>
          <pre className="bg-white border border-[#ECECEC] p-5 text-xs font-mono overflow-x-auto leading-relaxed text-[#0A0A0A] shadow-sm rounded-none" data-testid="apikey-curl-example">
{`# Read full catalogue with trade prices
curl '${API}/api/products' \\
  -H 'X-API-Key: fedyk_YOUR_KEY_HERE'

# Get a single product
curl '${API}/api/products/{product_id}' \\
  -H 'Authorization: Bearer fedyk_YOUR_KEY_HERE'

# Browse the OpenAPI spec
${API}/api/docs`}
          </pre>
        </div>
      </div>

      <CreateKeyModal
        key={modalOpen ? 'apikey-modal-open' : 'apikey-modal-closed'}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onCreated={fetchKeys}
        token={token}
        l={l}
      />
    </div>
  );
};

export default B2BApiKeysPage;
